import { useCallback, useEffect } from 'react';

import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { NAVBAR_ROUTES } from '@/shared/config/router/constants';
import { ROUTE_PATHS } from '@/shared/config/router/types';
import { isDev } from '@/shared/helpers';
import { getOrCreateElement, removeElement } from '@/shared/helpers/dom';
import { isTMA } from '@/shared/helpers/is-tma';
import { logErrorMini } from '@/shared/services/service-logger/lib/log-error-mini';
import { useAppActions, useSingletons } from '@/shared/store/app-store/app-store';
import { SingletonName } from '@/shared/store/app-store/types';
import { useBackButtonProps, useTgActions } from '@/shared/store/tg-store/tg-store';

import {
  DEFAULT_TOP_SAFE_AREA_OFFSET_PX,
  DEV_BACK_BUTTON_ID,
  TOP_SAFE_AREA_WRAPPER_ID,
} from './constants';

/**
 * Custom hook to handle Telegram's WebApp back button.
 * Singleton
 */
export const useTelegramBackButton = (): void => {
  const singleton = useSingletons();
  const { updateSingletons } = useAppActions();
  const { t } = useTranslation();

  const { updateBackButton } = useTgActions();
  const backButtonProps = useBackButtonProps();

  const location = useLocation();
  const navigate = useNavigate();

  const IS_DEV_ENV = import.meta.env.DEV && !isTMA();

  const hasBackAction =
    location.pathname !== ROUTE_PATHS.MAIN &&
    location.pathname !== ROUTE_PATHS.DAYS_STREAK;

  const checkBackButton = () => {
    if (!isTMA()) return;

    if (hasBackAction) WebApp.BackButton.show();
    else WebApp.BackButton.hide();
  };

  const goBack = useCallback(() => {
    const { customAction } = backButtonProps;

    if (customAction) {
      customAction?.();
      return;
    }

    const redirectInsideNavbar =
      NAVBAR_ROUTES.includes(location.pathname) &&
      !location.state?.ignoringNavbarRedirect;

    if (redirectInsideNavbar) {
      navigate(ROUTE_PATHS.MAIN);
      return;
    }

    window.history.back();
  }, [
    backButtonProps,
    location.pathname,
    location.state?.ignoringNavbarRedirect,
    navigate,
  ]);

  /**
   * Configures the fake back button in development mode.
   */
  const setupDevBackButton = useCallback(
    (hasBackAction: boolean) => {
      if (!IS_DEV_ENV) return;

      const container = getOrCreateElement(TOP_SAFE_AREA_WRAPPER_ID);
      container.className = 'top-safe-area-wrapper';

      document.documentElement.style.setProperty(
        '--tg-content-safe-area-inset-top',
        `${DEFAULT_TOP_SAFE_AREA_OFFSET_PX}px`
      );

      const button = getOrCreateElement(DEV_BACK_BUTTON_ID, container, 'button');
      button.className = 'dev-back-button';
      button.classList.toggle('close', !hasBackAction);
      button.textContent = hasBackAction ? t('Back') : t('Close');

      if (hasBackAction) button.addEventListener('click', goBack);
    },
    [IS_DEV_ENV, goBack, t]
  );

  useEffect(() => {
    isTMA() && WebApp.BackButton.onClick(goBack);
    document.getElementById(DEV_BACK_BUTTON_ID)?.addEventListener('click', goBack);

    return () => {
      isTMA() && WebApp.BackButton.offClick(goBack);
      document.getElementById(DEV_BACK_BUTTON_ID)?.removeEventListener('click', goBack);
    };
  }, [goBack]);

  useEffect(() => {
    if (backButtonProps.customAction) {
      updateBackButton({ customAction: null });
    }

    checkBackButton();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (singleton[SingletonName.BACK_BUTTON]) {
      if (isDev('both') && !IS_DEV_ENV) {
        throw new Error(
          'The back button has already been created. useTelegramBackButton is singleton'
        );
      }

      void logErrorMini(
        'The useTelegramBackButton is used multiple times',
        'use-telegram-back-button.ts'
      );
    } else {
      updateSingletons({ [SingletonName.BACK_BUTTON]: true });
    }

    const handlePageShow = (e: PageTransitionEvent) => {
      WebApp.unlockOrientation();
      // проверяем была ли открыта раннее страница
      if (e.persisted && isTMA()) {
        // если да, то показываем кнопку т.к при редиректах между страницами стейт кнопки сбрасывается для телеграма (но визуально не меняется)
        // если кнопка была открыта до редиректа, то будет показано, но не работать
        WebApp.BackButton.show();
        checkBackButton();
      }
    };

    // отслеживаем факт переоткрытие страницы
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      removeElement(
        DEV_BACK_BUTTON_ID,
        document.getElementById(TOP_SAFE_AREA_WRAPPER_ID) as HTMLElement
      );

      window.removeEventListener('pageshow', handlePageShow);

      isTMA() && WebApp.BackButton.hide();

      updateSingletons({ [SingletonName.BACK_BUTTON]: false });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setupDevBackButton(hasBackAction);
  }, [IS_DEV_ENV, hasBackAction, location.pathname, setupDevBackButton, t]);
};
